import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

interface SideBarLayoutProps {
  children: React.ReactNode;
  initialCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  buttonClassName?: string;
  containerClassName?: string;
  toggleLabel?: string;
}

const DESKTOP_MIN_WIDTH = 992;
const isDesktopViewport = () => typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH;

const SideBarLayout: React.FC<SideBarLayoutProps> = ({
  children,
  initialCollapsed,
  onCollapseChange,
  buttonClassName = '',
  containerClassName = '',
  toggleLabel = 'Browse sections',
}) => {
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport);
  const [collapsed, setCollapsed] = useState(() => initialCollapsed ?? !isDesktopViewport());

  useEffect(() => {
    const onResize = () => {
      const desktop = isDesktopViewport();
      setIsDesktop(prev => {
        if (prev !== desktop) setCollapsed(!desktop);
        return desktop;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      onCollapseChange?.(next);
      return next;
    });
  };

  const handleNavClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isDesktop) return;
    if ((event.target as HTMLElement).closest('a')) {
      setCollapsed(true);
      onCollapseChange?.(true);
    }
  };

  return (
    <div className={`lg:relative ${containerClassName}`}>
      {!isDesktop && (
        <button
          type="button"
          onClick={toggleCollapsed}
          className={`mb-3 flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 shadow-sm ${buttonClassName}`}
          aria-expanded={!collapsed}
          title={collapsed ? 'Open sections' : 'Close sections'}
        >
          <span>{toggleLabel}</span>
          {collapsed ? <ChevronDownIcon className="h-5 w-5" /> : <ChevronUpIcon className="h-5 w-5" />}
        </button>
      )}

      {(isDesktop || !collapsed) && (
        <div className="mb-6 w-full lg:mb-0" onClick={handleNavClick}>
          <div className="lg:sticky lg:top-4">{children}</div>
        </div>
      )}
    </div>
  );
};

export default SideBarLayout;
